'use client'

import { useState, type FormEvent } from 'react'

/**
 * The newsletter sign-up in the home page's closing section (08). It moved
 * here from the footer on 2026-10-09 (Lloyd): same endpoint and messages as
 * the footer form it replaces, in the section's own `form.domain` pill.
 * `source` keeps these sign-ups apart from the old footer ones in
 * /admin/subscribers.
 *
 * The wrapper carries the scroll reveal, not the form: the form is swapped
 * for the confirmation, and a node that mounts after ShellEffects ran would
 * otherwise stay hidden until the next scroll.
 */
export default function HomeNewsletterForm() {
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [message, setMessage] = useState('')

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!email || isSubmitting) return
    setIsSubmitting(true)
    setStatus('idle')
    try {
      const response = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'home_newsletter' }),
      })
      const data = await response.json()
      if (response.ok) {
        setStatus('success')
        setMessage(data.message || "You're subscribed!")
        setEmail('')
      } else {
        setStatus('error')
        setMessage(data.message || 'Something went wrong. Please try again.')
      }
    } catch {
      setStatus('error')
      setMessage('Network error. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div data-reveal='fade'>
      {status === 'success' ? (
        <p className='cta__note' role='status' aria-live='polite'>
          {message}
        </p>
      ) : (
        <form className='domain' onSubmit={handleSubmit}>
          <input
            type='email'
            name='email'
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder='Enter your email'
            aria-label='Email for newsletter'
            autoComplete='email'
            disabled={isSubmitting}
            required
          />
          <button
            className='btn btn--accent'
            type='submit'
            disabled={isSubmitting || !email}
          >
            {isSubmitting ? 'Subscribing…' : 'Subscribe'}{' '}
            <svg
              className='arrow'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='1.8'
              aria-hidden='true'
            >
              <path d='M5 12h14M13 6l6 6-6 6' />
            </svg>
          </button>
        </form>
      )}
      {status === 'error' && (
        <p className='cta__note' role='alert'>
          {message}
        </p>
      )}
    </div>
  )
}
